import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Panel } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Divider } from 'primeng/divider';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectLccaData, selectLccaTotal } from '../store/common.selector';
import { Lcca } from '../../shared/models/common/lcca.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseListComponent } from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';
import { TypeAutocomplete2Component } from '../../shared/components/form-input/type-autocomplete-2/type-autocomplete-2.component';
import { LccaTypeAutocompleteComponent } from '../../shared/components/form-input/lcca-type-autocomplete/lcca-type-autocomplete.component';
import { LccaPeriodicalAutocompleteComponent } from '../../shared/components/form-input/lcca-periodical-autocomplete/lcca-periodical-autocomplete.component';
import { LccaUnitAutocompleteComponent } from '../../shared/components/form-input/lcca-unit-autocomplete/lcca-unit-autocomplete.component';
import { CommonService } from '../../shared/services/common.service';

@Component({
  selector: 'lcca-list',
  templateUrl: './lcca-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    TypeAutocomplete2Component,
    LccaTypeAutocompleteComponent,
    LccaPeriodicalAutocompleteComponent,
    LccaUnitAutocompleteComponent,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./lcca-list.component.scss']
})
export class LccaListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);
  private commonService = inject(CommonService);

  lccas = toSignal(this.store$.select(selectLccaData), { initialValue: [] });
  totalRecords = toSignal(this.store$.select(selectLccaTotal), { initialValue: 0 });
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Asset Codes')), { initialValue: false });

  
  readonly auditResourceName = 'Lcca';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedLccas = signal<Lcca[]>([]);

  // Lookup maps for displaying names instead of IDs
  assetTypeMap = signal<Map<number, string>>(new Map());
  maintenanceTypeMap = signal<Map<number, string>>(new Map());
  periodicalMap = signal<Map<number, string>>(new Map());
  unitMap = signal<Map<number, string>>(new Map());

  // Add Asset Code Form Variables
  dialogVisible = signal(false);
  assetType = signal<string | number>('');
  lccaCode = signal('');
  maintenanceDetail = signal('');
  maintenanceType = signal<string | number>('');
  frequencyValue = signal('');
  frequencyPeriod = signal<string | number>('');
  trigger = signal('');
  rate = signal('');
  unit = signal<string | number>('');

  savingLcca = signal(false);
  isEditMode = signal(false);
  editingLccaId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedLccas().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Load lookup data for displaying names
    this.loadLookupData();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createLccaSuccess, action.updateLccaSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLcca.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteLccaSuccess, action.bulkDeleteLccasSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedLccas.set([]);
      this.loadLccas();
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createLccaError, action.updateLccaError, action.deleteLccaError, action.bulkDeleteLccasError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLcca.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private loadLookupData(): void {
    // Load asset types
    this.commonService.findAssetCodes(1, 100, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: any) => map.set(item.id, item.asset_type_name || item.asset_type_code || ''));
      this.assetTypeMap.set(map);
    });

    // Load maintenance types
    this.commonService.findLccaTypes(1, 100, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: any) => map.set(item.id, item.name || ''));
      this.maintenanceTypeMap.set(map);
    });

    // Load periodicals
    this.commonService.findLccaPeriodicals(1, 100, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: any) => map.set(item.id, item.name || ''));
      this.periodicalMap.set(map);
    });

    // Load units
    this.commonService.findLccaUnits(1, 100, '').pipe(takeUntil(this.destroy$)).subscribe(result => {
      const map = new Map<number, string>();
      (result.data || []).forEach((item: any) => map.set(item.id, item.name || ''));
      this.unitMap.set(map);
    });
  }

  getAssetTypeName(id: number | undefined): string {
    if (!id) return '';
    return this.assetTypeMap().get(id) || String(id);
  }

  getMaintenanceTypeName(id: number | undefined): string {
    if (!id) return '';
    return this.maintenanceTypeMap().get(id) || String(id);
  }

  getPeriodicalName(id: number | undefined): string {
    if (!id) return '';
    return this.periodicalMap().get(id) || String(id);
  }

  getUnitName(id: number | undefined): string {
    if (!id) return '';
    return this.unitMap().get(id) || String(id);
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findLccas({
      page,
      size,
      keywords
    }));
  }

  loadLccas(): void {
    this.store$.dispatch(action.findLccas({ page: 1, size: this.rows, keywords: '' }))
  }

  addNew(): void {
    this.lccaCode.set('');
    this.maintenanceDetail.set('');
    this.maintenanceType.set('');
    this.frequencyValue.set('');
    this.frequencyPeriod.set('');
    this.trigger.set('');
    this.rate.set('');
    this.unit.set('');
    this.assetType.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingLccaId.set(null);
  }

  closeDialog(): void {
    this.lccaCode.set('');
    this.maintenanceDetail.set('');
    this.maintenanceType.set('');
    this.frequencyValue.set('');
    this.frequencyPeriod.set('');
    this.trigger.set('');
    this.rate.set('');
    this.unit.set('');
    this.assetType.set('');
    this.savingLcca.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingLccaId.set(null);
  }

  saveLcca(): void {
    const assetTypeValue = this.assetType();
    const assetTypeValid = typeof assetTypeValue === 'number' ? assetTypeValue > 0 : (typeof assetTypeValue === 'string' && assetTypeValue.trim() !== '');

    const maintenanceTypeValue = this.maintenanceType();
    const maintenanceTypeValid = typeof maintenanceTypeValue === 'number' ? maintenanceTypeValue > 0 : (typeof maintenanceTypeValue === 'string' && maintenanceTypeValue.trim() !== '');

    const frequencyPeriodValue = this.frequencyPeriod();
    const frequencyPeriodValid = typeof frequencyPeriodValue === 'number' ? frequencyPeriodValue > 0 : (typeof frequencyPeriodValue === 'string' && frequencyPeriodValue.trim() !== '');

    const unitValue = this.unit();
    const unitValid = typeof unitValue === 'number' ? unitValue > 0 : (typeof unitValue === 'string' && unitValue.trim() !== '');

    if (!this.lccaCode().trim()                 // no. input
      || !this.maintenanceDetail().trim()
      || !maintenanceTypeValid
      || !this.frequencyValue().trim()
      || !frequencyPeriodValid
      || !this.trigger().trim()
      || !this.rate().trim()
      || !unitValid
      || !assetTypeValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Lcca details are required'
      });
      return;
    }

    this.savingLcca.set(true);

    const lccaData: Lcca = {
      id: this.editingLccaId() || 0,
      lcca_code: this.lccaCode().trim(),
      maintenance_detail: this.maintenanceDetail().trim(),
      maintenanc_type: typeof maintenanceTypeValue === 'number' ? maintenanceTypeValue : (Number(maintenanceTypeValue) || undefined),
      frequency_value: Number(this.frequencyValue()) || undefined,
      frequency_period: typeof frequencyPeriodValue === 'number' ? frequencyPeriodValue : (Number(frequencyPeriodValue) || undefined),
      trigger: Number(this.trigger()) || undefined,
      rate: Number(this.rate()) || undefined,
      unit: typeof unitValue === 'number' ? unitValue : (Number(unitValue) || undefined),
      asset_type: typeof assetTypeValue === 'number' ? assetTypeValue : (Number(assetTypeValue) || undefined)
    };

    if (this.isEditMode() && this.editingLccaId()) {
      this.store$.dispatch(action.updateLcca({ id: this.editingLccaId()!, lcca: lccaData }))
    } else {
      this.store$.dispatch(action.createLcca({ lcca: lccaData }))
    }
  }

  editLcca(): void {
    if (this.selectedLccas().length === 1) {
      const lccaToEdit = this.selectedLccas()[0];
      this.lccaCode.set(lccaToEdit.lcca_code ? lccaToEdit.lcca_code : '');
      this.maintenanceDetail.set(lccaToEdit.maintenance_detail ? lccaToEdit.maintenance_detail : '');
      this.maintenanceType.set(lccaToEdit.maintenanc_type != null ? lccaToEdit.maintenanc_type : '');
      this.frequencyValue.set(lccaToEdit.frequency_value != null ? String(lccaToEdit.frequency_value) : '');
      this.frequencyPeriod.set(lccaToEdit.frequency_period != null ? lccaToEdit.frequency_period : '');
      this.trigger.set(lccaToEdit.trigger != null ? String(lccaToEdit.trigger) : '');
      this.rate.set(lccaToEdit.rate != null ? String(lccaToEdit.rate) : '');
      this.unit.set(lccaToEdit.unit != null ? lccaToEdit.unit : '');
      this.assetType.set(lccaToEdit.asset_type != null ? lccaToEdit.asset_type : '');
      this.isEditMode.set(true);
      this.editingLccaId.set(lccaToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one asset code to edit'
      });
    }
  }

  deleteLcca(): void {
    if (this.selectedLccas().length === 0) {
      return;
    }

    const lccasToDelete = [...this.selectedLccas()];
    const count = lccasToDelete.length;

    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${lccasToDelete[0].maintenance_detail}" (${lccasToDelete[0].lcca_code})?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = lccasToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((ac, index) => `${index + 1}. ${ac.lcca_code} - ${ac.maintenance_detail}`).join('<br>');

      let fullMessage = `Are you sure you want to delete ${count} asset codes?<br><br><strong>Asset codes to be deleted:</strong><br>${codesList}`;

      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }

      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteLcca({ id: lccasToDelete[0].id }));
        } else {
          const ids = lccasToDelete.map(ac => ac.id);
          this.store$.dispatch(action.bulkDeleteLccas({ ids }));
        }
      }
    });
  }

  refreshLccas(): void {
    this.selectedLccas.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Asset codes list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedLccas();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.lcca_name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
